import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import { IoChevronDown, IoChevronUp, IoClose } from 'react-icons/io5';

const ReportSearchFilter = ({
  filtersConfig,
  onFilterChange,
  onSearchChange,
  className = '',
  inputClassName = 'form-control inputdesign',
  inputStyle = {
    fontSize: '0.8rem',
    fontWeight: '500',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    padding: '0.375rem 0.75rem',
    height: '30px'
  }
}) => {
  const initialFilterValues = filtersConfig.reduce((acc, filter) => {
    acc[filter.name] = filter.multiSelect ? (filter.defaultValue || []) : (filter.defaultValue || '');
    return acc;
  }, {});

  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [focusedSelects, setFocusedSelects] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState({});
  const selectRefs = useRef({});
  const searchInputRefs = useRef({});

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(initialFilterValues);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    const newValues = { ...filterValues, [name]: newValue };
    setFilterValues(newValues);
    if (onFilterChange) {
      onFilterChange(newValues);
    }
    setDropdownOpen(prev => ({ ...prev, [name]: false }));
  };

  const handleSearchChange = (name, value) => {
    setSearchTerms(prev => ({ ...prev, [name]: value }));
    if (onSearchChange) {
      onSearchChange(name, value);
    }
  };

  const handleSelectFocus = (name) => {
    setFocusedSelects(prev => ({ ...prev, [name]: true }));
  };

  const handleSelectBlur = (name) => {
    setFocusedSelects(prev => ({ ...prev, [name]: false }));
  };

  const toggleDropdown = (name) => {
    setDropdownOpen(prev => ({ ...prev, [name]: !prev[name] }));
    if (!dropdownOpen[name]) {
      setTimeout(() => {
        searchInputRefs.current[name]?.focus();
      }, 0);
    }
  };

  const clearSelection = (name, e) => {
    e.stopPropagation();
    const filter = filtersConfig.find(f => f.name === name);
    let defaultValue;
    
    if (filter.type === 'checkbox') {
      defaultValue = 0;
    } else if (filter.multiSelect) {
      defaultValue = [];
    } else {
      defaultValue = '';
    }
    
    const newValues = { ...filterValues, [name]: defaultValue };
    setFilterValues(newValues);
    setSearchTerms(prev => ({ ...prev, [name]: '' }));
    if (onFilterChange) {
      onFilterChange(newValues);
    }
  };

  const toggleOptionSelection = (name, optionValue) => {
    const currentValues = filterValues[name] || [];
    const isMultiSelect = filtersConfig.find(f => f.name === name)?.multiSelect;

    let newValues;
    if (isMultiSelect) {
      newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
    } else {
      newValues = optionValue;
    }

    const updatedValues = { ...filterValues, [name]: newValues };
    setFilterValues(updatedValues);
    setSearchTerms(prev => ({ ...prev, [name]: '' }));
    if (onFilterChange) {
      onFilterChange(updatedValues);
    }

    if (!isMultiSelect) {
      setDropdownOpen(prev => ({ ...prev, [name]: false }));
    }
  };

  const commonInputStyles = {
    ...inputStyle,
    width: '100%',
    boxSizing: 'border-box',
    height: '30px',
    lineHeight: '1.5',
    padding: '0.25rem 0.5rem',
    marginTop: '4px'
  };

  const filteredOptions = (filter) => {
    if (!searchTerms[filter.name]) return filter.options || [];
    return (filter.options || []).filter(option =>
      option.label.toLowerCase().includes(searchTerms[filter.name].toLowerCase())
    );
  };

  const isOptionSelected = (filterName, optionValue) => {
    const currentValues = filterValues[filterName];
    return Array.isArray(currentValues)
      ? currentValues.includes(optionValue)
      : currentValues === optionValue;
  };

  const getSelectedLabels = (filter) => {
    const selected = filterValues[filter.name];

    if (filter.multiSelect) {
      if (!Array.isArray(selected) || selected.length === 0) return '';
      if (selected.length === 1) {
        const foundOption = filter.options?.find(opt => opt.value === selected[0]);
        return foundOption ? foundOption.label : '';
      }
      return `${selected.length} selected`;
    }

    if (filter.type === 'checkbox') {
      return selected ? 'Yes' : 'No';
    }

    if (selected === '' || selected === null || selected === undefined) return '';
    const foundOption = filter.options?.find(opt => opt.value === selected);
    return foundOption ? foundOption.label : '';
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      Object.keys(dropdownOpen).forEach(name => {
        if (
          dropdownOpen[name] &&
          !selectRefs.current[name]?.contains(e.target) &&
          !searchInputRefs.current[name]?.contains(e.target)
        ) {
          setDropdownOpen(prev => ({ ...prev, [name]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  return (
    <>
      <style>{`
        /* Scrollbar styling for webkit browsers */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f0f0f0;
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #e69500;
          border-radius: 4px;
          border: 2px solid #f0f0f0;
        }

        /* Firefox scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #e69500 #f0f0f0;
        }
        
        .checkbox-container {
          display: flex;
          align-items: center;
          height: 30px;
          margin-top: 4px;
        }
        
        .checkbox-container input[type="checkbox"] {
          margin-right: 8px;
          width: 16px;
          height: 16px;
          cursor: pointer;
        }
        
        .checkbox-container label {
          cursor: pointer;
          font-size: 0.8rem;
          color: #495057;
        }
      `}</style>
      <div
        className={className}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: '0 6px',
          marginBottom: '15px',
          alignItems: 'flex-start',
          gap: '2px'
        }}
      >
        {filtersConfig.map(filter => {
          const value = filterValues[filter.name];
          const isActive =
            (Array.isArray(value) && value.length > 0) || 
            (!Array.isArray(value) && value !== '' && value !== 0) || 
            dropdownOpen[filter.name] || 
            focusedSelects[filter.name];

          return (
            <div
              key={filter.name}
              style={{
                width: filter.width || '180px',
                flexShrink: 0,
                position: 'relative',
                marginBottom: '5px',
                marginRight: '5px'
              }}
              ref={el => (selectRefs.current[filter.name] = el)}
            >
              {filter.type !== 'checkbox' && (
                <label
                  style={{
                    position: 'absolute',
                    top: isActive ? '0px' : '12px',
                    left: '8px',
                    fontSize: isActive ? '0.7rem' : '0.8rem',
                    color: isActive ? '#495057' : '#6c757d',
                    backgroundColor: 'white',
                    padding: '0 4px',
                    zIndex: 1,
                    transition: 'all 0.15s ease-out',
                    pointerEvents: 'none',
                    lineHeight: '1'
                  }}
                >
                  {filter.label || filter.placeholder}
                </label>
              )}

              {filter.type === 'selectsearch' ? (
                <div style={{ position: 'relative' }}>
                  <div
                    className={`${inputClassName} custom-select`}
                    style={{
                      ...commonInputStyles,
                      appearance: 'none',
                      paddingRight: '25px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      borderColor: dropdownOpen[filter.name] ? '#80bdff' : '#ced4da',
                      boxShadow: dropdownOpen[filter.name] ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none'
                    }}
                    onClick={() => toggleDropdown(filter.name)}
                  >
                    <span style={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getSelectedLabels(filter)}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {(Array.isArray(value) ? value.length > 0 : value) && (
                        <IoClose
                          size={14}
                          style={{ marginLeft: '5px', color: '#6c757d' }}
                          onClick={(e) => clearSelection(filter.name, e)}
                        />
                      )}
                      <div style={{ marginLeft: '5px' }}>
                        {dropdownOpen[filter.name] ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />}
                      </div>
                    </div>
                  </div>

                  {dropdownOpen[filter.name] && (
                    <div
                      className="custom-scrollbar"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div
                        style={{
                          padding: '6px',
                          borderBottom: '1px solid #eee',
                          backgroundColor: 'white',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                      >
                        <input
                          ref={el => (searchInputRefs.current[filter.name] = el)}
                          type="text"
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '3px',
                            fontSize: '0.8rem',
                            outline: 'none',
                            height: '28px'
                          }}
                          placeholder={`Search ${filter.label}...`}
                          value={searchTerms[filter.name] || ''}
                          onChange={(e) => handleSearchChange(filter.name, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div>
                        {filteredOptions(filter).length > 0 ? (
                          filteredOptions(filter).map(option => (
                            <div
                              key={option.value}
                              style={{
                                fontSize: '0.8rem',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                backgroundColor: isOptionSelected(filter.name, option.value) ? '#f8f9fa' : 'white',
                                borderLeft: isOptionSelected(filter.name, option.value)
                                  ? '3px solid #e69500'
                                  : '3px solid transparent'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOptionSelection(filter.name, option.value);
                              }}
                            >
                              {filter.multiSelect && (
                                <input
                                  type="checkbox"
                                  checked={isOptionSelected(filter.name, option.value)}
                                  readOnly
                                  style={{
                                    marginRight: '8px',
                                    cursor: 'pointer',
                                    accentColor: isOptionSelected(filter.name, option.value) ? '#391f84' : '#6c757d'
                                  }}
                                />
                              )}
                              {option.label}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '10px', color: '#6c757d', textAlign: 'center' }}>
                            No options found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : filter.type === 'select' ? (
                <div style={{ position: 'relative' }}>
                  <select
                    name={filter.name}
                    className={`${inputClassName} custom-select`}
                    style={{
                      ...commonInputStyles,
                      appearance: 'none',
                      paddingRight: '25px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      borderColor: focusedSelects[filter.name] ? '#80bdff' : '#ced4da',
                      boxShadow: focusedSelects[filter.name] ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none'
                    }}
                    value={filterValues[filter.name]}
                    onChange={handleChange}
                    onFocus={() => handleSelectFocus(filter.name)}
                    onBlur={() => handleSelectBlur(filter.name)}
                  >
                    <option value=""></option>
                    {filter.options?.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ position: 'absolute', right: '8px', top: '0px', pointerEvents: 'none', color: '#6c757d' }}>
                    <IoChevronDown size={14} />
                  </div>
                </div>
              ) : filter.type === 'checkbox' ? (
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    name={filter.name}
                    id={`checkbox-${filter.name}`}
                    checked={!!filterValues[filter.name]}
                    onChange={handleChange}
                    style={{
                      accentColor: '#391f84',
                      cursor: 'pointer',
                      width : '15px',
                      height : '15px'
                    }}
                  />
                  <label htmlFor={`checkbox-${filter.name}`}>
                    {filter.label || filter.placeholder}
                  </label>
                </div>
              ) : (
                <input
                  type={filter.type || 'text'}
                  name={filter.name}
                  className={inputClassName}
                  style={{
                    ...commonInputStyles,
                    borderColor: focusedSelects[filter.name] ? '#80bdff' : '#ced4da',
                    boxShadow: focusedSelects[filter.name] ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
                    paddingTop: '6px'
                  }}
                  value={filterValues[filter.name]}
                  onChange={handleChange}
                  onFocus={() => handleSelectFocus(filter.name)}
                  onBlur={() => handleSelectBlur(filter.name)}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

ReportSearchFilter.propTypes = {
  filtersConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.oneOf(['text', 'date', 'number', 'select', 'selectsearch', 'checkbox']),
      placeholder: PropTypes.string,
      width: PropTypes.string,
      defaultValue: PropTypes.any,
      multiSelect: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired
        })
      )
    })
  ).isRequired,
  onFilterChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  inputStyle: PropTypes.object
};

ReportSearchFilter.defaultProps = {
  filtersConfig: [],
  inputClassName: 'form-control inputdesign',
  inputStyle: {
    fontSize: '0.8rem',
    fontWeight: '500',
    borderRadius: '4px',
    height: '30px'
  }
};

export default ReportSearchFilter;